import {
  createComponent,
  DestroyRef,
  Directive,
  EnvironmentInjector,
  inject,
  Injector,
  input,
  output,
  ViewContainerRef,
} from '@angular/core';
import { nodeEditorComponentConfig } from '../configs/node-editor-components.config';
import { FlowNode, NodeEditorComponentType } from '../interfaces/flow-ui.interface';
import { NodeType } from '../interfaces/node.interface';
import { TriggerNodesEnum } from '../services/graph.utils';

@Directive({
  selector: '[nodeEditorComponent]',
  standalone: true,
})
export class NodeEditorComponentDirective {
  public nodeEditorType = input<FlowNode, FlowNode>(undefined, {
    alias: 'nodeEditorComponent',
    transform: (node) => {
      if (node) {
        this.loadNodeEditorComponent(node);
      }

      return node;
    },
  });

  public updateValue = output<unknown>();
  public componentIsRendered = output<void>();
  public componentInstance!: NodeEditorComponentType;

  private nodeEditorComponentConfig = nodeEditorComponentConfig;
  private destroyed = false;

  private viewContainerRef = inject(ViewContainerRef);
  private environmentInjector = inject(EnvironmentInjector);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
    });
  }

  private loadNodeEditorComponent(node: FlowNode): void {
    let nodeType = node.nodeData.type;

    switch (nodeType) {
      case NodeType.Trigger:
        switch (node.nodeData.name) {
          case TriggerNodesEnum.Schedule:
            nodeType = NodeType.Scheduler;
            break;
          case TriggerNodesEnum.Webhook:
            nodeType = NodeType.Webhook;
            break;
          case TriggerNodesEnum.Api:
            nodeType = NodeType.Api;
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    const nodeEditorComponent = this.nodeEditorComponentConfig[nodeType];

    this.viewContainerRef.clear();

    if (nodeEditorComponent) {
      nodeEditorComponent().then((component) => {
        const componentReference = createComponent(component, {
          elementInjector: this.injector,
          environmentInjector: this.environmentInjector,
        });

        this.componentInstance = componentReference.instance;
        this.componentInstance.updateValue.subscribe((value) => {
          if (!this.destroyed) {
            this.updateValue.emit(value);
          }
        });

        this.componentInstance.componentIsRendered.subscribe(() => {
          if (!this.destroyed) {
            this.componentIsRendered.emit();
          }
        });

        componentReference.setInput('node', node);
        this.viewContainerRef.insert(componentReference.hostView);
      });
    }
  }
}
